import React from 'react';
import { getSyncState } from '../hooks/useFirestoreSyncMonitor';

/**
 * AppErrorBoundary — top-level React error boundary (Walter 2026-06-13).
 *
 * Before this, the app had NO error boundary, so ANY render-time crash
 * (e.g. the Sidebar #300 hooks-mismatch that fired on an anonymous→real
 * sign-in while Firestore was blocked) white-screened the entire app with
 * no recovery. This catches those crashes and shows a recovery card with a
 * Reload button.
 *
 * Most crashes we'll actually see in the wild trace back to an ad-blocker /
 * privacy extension blocking Firestore's WebChannel — the app can't reach
 * its database, components render against never-arriving data, and something
 * downstream throws. So when the sync monitor tells us the connection looks
 * blocked, we lead with the "whitelist listicity.app" guidance (the real
 * fix); otherwise a generic "reload" recovery with the ad-blocker hint as a
 * secondary line.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '', stack: '', showDetails: false };
  }

  static getDerivedStateFromError(error) {
    // Walter 2026-06-16: capture the actual error so it can be SHOWN on the
    // card. On mobile there's no console, so a "something went wrong" card
    // with no detail is undiagnosable — a tester (or Walter) can't tell us
    // what threw. Now they can tap "show details" and read/screenshot it.
    return {
      hasError: true,
      message: (error && (error.message || String(error))) || 'unknown error',
      stack: (error && error.stack) || '',
    };
  }

  componentDidCatch(error, info) {
    const componentStack = (info && info.componentStack) || '';
    this.setState({ componentStack });
    try {
      // eslint-disable-next-line no-console
      console.error('[listicity] render crash caught by AppErrorBoundary:', error, componentStack);
    } catch { /* ignore */ }

    // Walter 2026-06-16: AUTO-RECOVER the stale-bundle / chunk-load crash
    // class. After a deploy, a PWA can serve a cached index.html that
    // references a bundle hash since GC'd from Hosting → the JS request gets
    // SPA-rewritten to HTML → "Unexpected token '<'" (or a ChunkLoadError) →
    // this boundary fires. The fix the user discovers is "reload" — so do it
    // for them: purge the SW caches (which hold the stale shell) and reload
    // once. Throttled to once / 30s so a DETERMINISTIC bug can't loop —
    // after the one attempt it falls through to the visible card. Only the
    // stale-bundle signature triggers this; real bugs show the card at once.
    const msg = (error && (error.message || String(error))) || '';
    const staleBundle = /unexpected token|chunk ?load|loading chunk|dynamically imported module|mime type|importing a module script|failed to fetch|<\s*$|expected expression|illegal character/i.test(msg);
    if (!staleBundle) return;
    let lastAt = 0;
    try { lastAt = parseInt(sessionStorage.getItem('lists_crash_autorecover_at') || '0', 10); } catch { /* ignore */ }
    if (Date.now() - lastAt < 30000) return; // already tried recently → show card
    try { sessionStorage.setItem('lists_crash_autorecover_at', String(Date.now())); } catch { /* ignore */ }
    this.purgeAndReload();
  }

  purgeAndReload() {
    const reload = () => { try { window.location.reload(); } catch { /* ignore */ } };
    try {
      const tasks = [];
      if (typeof caches !== 'undefined' && caches.keys) {
        tasks.push(caches.keys().then(ks => Promise.all(ks.map(k => caches.delete(k)))).catch(() => {}));
      }
      if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
        tasks.push(navigator.serviceWorker.getRegistrations()
          .then(rs => Promise.all(rs.map(r => (r.update ? r.update().catch(() => {}) : null))))
          .catch(() => {}));
      }
      Promise.all(tasks).then(reload, reload);
      // Safety: reload even if a purge step hangs (e.g. iOS caches API quirk).
      setTimeout(reload, 2500);
    } catch { reload(); }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Is the crash likely an ad-blocker blocking Firestore? connectionBlocked
    // is the watchdog's considered verdict; never-synced-while-online is the
    // strong corroborating signal (we got far enough to crash but never saw
    // server data). Either way the copy is hedged ("if you use an ad
    // blocker…") so a mis-attribution still reads sensibly.
    let blocked = false;
    try {
      const s = getSyncState();
      const online = typeof navigator === 'undefined' || navigator.onLine !== false;
      blocked = !!s && (s.connectionBlocked === true || (s.everSynced === false && online));
    } catch { /* ignore */ }

    const reload = () => { try { window.location.reload(); } catch { /* ignore */ } };

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', background: 'var(--bg, #14120e)',
        fontFamily: 'var(--mono, monospace)',
      }}>
        <div style={{
          maxWidth: 460, width: '100%',
          background: 'var(--surface, #1f1c17)',
          border: '1px solid var(--border, #3a352c)',
          borderRadius: 'var(--radius, 8px)',
          padding: '22px 24px',
          color: 'var(--text, #e8e4d5)',
          lineHeight: 1.5,
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }}>
          <div style={{
            fontSize: 'calc(14px * var(--ui-scale, 1))',
            fontWeight: 600, marginBottom: '10px',
          }}>
            {blocked ? "listicity can't reach its database" : 'something went wrong'}
          </div>
          <div style={{
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            color: 'var(--text-dim, #a09d8d)', marginBottom: '18px',
          }}>
            {blocked ? (
              <>
                If you use an ad blocker or privacy extension (Ghostery,
                uBlock, Brave Shields…), it may be blocking the connection —
                whitelist <strong style={{ color: 'var(--text, #e8e4d5)' }}>listicity.app</strong> and reload.
              </>
            ) : (
              <>
                Reloading usually fixes it. If you use an ad blocker or
                privacy extension, whitelisting <strong style={{ color: 'var(--text, #e8e4d5)' }}>listicity.app</strong> can help too.
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={reload}
              style={{
                background: 'var(--accent, #e8622c)', color: 'var(--bg, #14120e)',
                border: '1px solid var(--accent, #e8622c)', borderRadius: 'var(--radius, 8px)',
                padding: '9px 18px', fontFamily: 'var(--mono, monospace)',
                fontSize: 'calc(12px * var(--ui-scale, 1))', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              reload
            </button>
            {/* Walter 2026-06-16: the actual error, on the card. No console on
                mobile, so this is the only way a phone crash becomes
                diagnosable — tap to reveal, then screenshot/read it to me. */}
            <button
              type="button"
              onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
              style={{
                background: 'transparent', color: 'var(--text-dim, #a09d8d)',
                border: '1px solid var(--border, #3a352c)', borderRadius: 'var(--radius, 8px)',
                padding: '9px 14px', fontFamily: 'var(--mono, monospace)',
                fontSize: 'calc(11px * var(--ui-scale, 1))', cursor: 'pointer',
              }}
            >
              {this.state.showDetails ? 'hide details' : 'show details'}
            </button>
          </div>

          {this.state.showDetails && (
            <pre style={{
              marginTop: 14,
              maxHeight: 220,
              overflow: 'auto',
              background: 'var(--surface2, #14120e)',
              border: '1px solid var(--border, #3a352c)',
              borderRadius: 'var(--radius, 8px)',
              padding: '10px 12px',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dim, #a09d8d)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              userSelect: 'text',
            }}>
              {this.state.message}
              {this.state.componentStack ? `\n${this.state.componentStack}` : ''}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
